// frontend/src/features/agency/AgencyGlobalResultsDrawer.js
// Agency "Search all statuses" drawer (global scope).
//
// NOTE: Unlike UPS, Agency lanes are assignment/ban derived, so we cannot rely on driver.status.
// We fetch the 4 lane read-model endpoints and filter client-side.

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { selectFilters, setScope } from '../ui/filtersSlice';

import {
  fetchAgencyActiveDrivers,
  fetchAgencyReservedDrivers,
  fetchAgencyAvailableDrivers,
  fetchAgencyBannedDrivers,
} from '../../services/driverService';

function filterRowsByQuery(rows, q) {
  const needle = (q ?? '').trim().toLowerCase();
  if (!needle) return rows || [];

  return (rows || []).filter((row) => {
    const haystacks = [
      row?.name,
      row?.vehicle_registration_number,
      row?.assignment_status,
      row?.assignment_id != null ? String(row.assignment_id) : null,
      row?.ban_reason,
      row?.reason,
      row?.ban_scope,
      row?.ban_site_id != null ? String(row.ban_site_id) : null,
      row?.ban_id != null ? String(row.ban_id) : null,
    ];

    return haystacks.some((v) => String(v ?? '').toLowerCase().includes(needle));
  });
}

function Section({ title, items, onViewAll }) {
  const count = items.length;

  return (
    <div className="border rounded-lg bg-white">
      <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
        <div className="text-sm font-semibold text-gray-800">
          {title} <span className="text-gray-500">({count})</span>
        </div>

        <button
          onClick={onViewAll}
          className="text-sm text-primary hover:underline disabled:opacity-50"
          disabled={count === 0}
          type="button"
        >
          View all
        </button>
      </div>

      {count === 0 ? (
        <div className="px-3 py-2 text-sm text-gray-500">No matches.</div>
      ) : (
        <div className="divide-y">
          {items.slice(0, 5).map((row) => (
            <div key={row.id ?? `${row.name}-${row.vehicle_registration_number}`} className="px-3 py-2">
              <div className="text-sm text-gray-900">{row.name || '—'}</div>
              <div className="text-xs text-gray-500">
                {row.vehicle_registration_number || '—'}
                {row.assignment_status ? ` • ${row.assignment_status} (#${row.assignment_id})` : ''}
              </div>
            </div>
          ))}

          {count > 5 && (
            <div className="px-3 py-2 text-xs text-gray-500">Showing first 5 of {count}.</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AgencyGlobalResultsDrawer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { q, scope } = useSelector(selectFilters);

  const show = scope === 'global' && q?.trim();
  const query = (q || '').trim();

  const [loading, setLoading] = useState(false);

  const [activeRows, setActiveRows] = useState([]);
  const [reservedRows, setReservedRows] = useState([]);
  const [availableRows, setAvailableRows] = useState([]);
  const [bannedRows, setBannedRows] = useState([]);

  // Debounced fetch when global search is active
  useEffect(() => {
    if (!show) return;

    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const [a, r, av, b] = await Promise.all([
          fetchAgencyActiveDrivers().catch(() => []),
          fetchAgencyReservedDrivers().catch(() => []),
          fetchAgencyAvailableDrivers().catch(() => []),
          fetchAgencyBannedDrivers().catch(() => []),
        ]);

        if (cancelled) return;

        setActiveRows(Array.isArray(a) ? a : []);
        setReservedRows(Array.isArray(r) ? r : []);
        setAvailableRows(Array.isArray(av) ? av : []);
        setBannedRows(Array.isArray(b) ? b : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250); // debounce typing

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [show, query]);

  const active = useMemo(() => filterRowsByQuery(activeRows, query), [activeRows, query]);
  const reserved = useMemo(() => filterRowsByQuery(reservedRows, query), [reservedRows, query]);
  const available = useMemo(() => filterRowsByQuery(availableRows, query), [availableRows, query]);
  const banned = useMemo(() => filterRowsByQuery(bannedRows, query), [bannedRows, query]);

  if (!show) return null;

  // TODO: adjust these routes to your actual Agency router paths.
  // Common patterns are:
  // - '/agency/drivers' (Active)
  // - '/agency/drivers/reserved'
  // - '/agency/drivers/available'
  // - '/agency/drivers/banned'
  const statusToRoute = {
    Active: '/agency/drivers',
    Reserved: '/agency/reserved',
    Available: '/agency/available',
    Banned: '/agency/banned',
  };


  const viewAllForStatus = (status) => {
    dispatch(setScope('tab'));
    const route = statusToRoute[status] || statusToRoute.Active;

    const params = new URLSearchParams();
    if (query) params.set('q', query);

    navigate(`${route}?${params.toString()}`);
  };

  const totalCount = active.length + reserved.length + available.length + banned.length;

  return (
    <div className="rounded-xl border bg-white shadow-sm p-3">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">Global results</div>
          <div className="text-xs text-gray-500">
            Searching “{query}” across statuses • {loading ? 'Searching…' : `${totalCount} match(es)`}
          </div>
        </div>

        <button
          type="button"
          onClick={() => dispatch(setScope('tab'))}
          className="text-sm text-gray-600 hover:underline"
        >
          Close
        </button>
      </div>

      {loading && (
        <div className="text-sm text-gray-500 mb-3">Loading results…</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Section title="Active" items={active} onViewAll={() => viewAllForStatus('Active')} />
        <Section title="Reserved" items={reserved} onViewAll={() => viewAllForStatus('Reserved')} />
        <Section title="Available" items={available} onViewAll={() => viewAllForStatus('Available')} />
        <Section title="Banned" items={banned} onViewAll={() => viewAllForStatus('Banned')} />
      </div>
    </div>
  );
}
